import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// import { useSpring, animated } from '@react-spring/web';
import { FormattedMessage } from 'react-intl';
import * as AppPropTypes from '../../lib/PropTypes';
import { useWindowSize } from '../../contexts/SiteContext';
import { getTextAtSpecificLength } from '../../lib/utils';

import Body from '../partials/Body';
import BackgroundText from '../partials/BackgroundText';
import LinedList from '../partials/LinedList';
import Image from '../partials/Image';
import RoundedButton from '../buttons/Rounded';
import ProjectCredit from '../partials/ProjectCredit';
import ProjectAward from '../partials/ProjectAward';
import Formats from '../partials/Formats';

import styles from '../../../styles/headers/project-header.module.scss';

const propTypes = {
    project: AppPropTypes.project.isRequired,
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const ProjectHeader = ({ project, className }) => {
    const imageRef = useRef(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const { width } = useWindowSize();

    const {
        title,
        headerImage = null,
        renderedFormats = null,
        summary = null,
        subtitle = null,
        broadcasters = null,
        clients = null,
        image = null,
        linkWithLabel = null,
        credits = null,
        awards = null,
        sections = [],
        categories = [],
        markets = [],
        platforms = [],
        agencies = [],
        theme,
    } = project || {};

    const { url = null, label = null } = linkWithLabel || {};
    const {
        backgroundText: decoText = null,
        logoAlignment = 'right',
        colors = null,
        elementsColor = null,
    } = theme || {};

    const textMaxLength = 800;
    const backgroundText =
        (decoText || summary) !== null
            ? getTextAtSpecificLength(textMaxLength, decoText || summary)
            : null;

    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => {
            // backgroundSpringApi.start(getBackgroundStyle());
            if (window.scrollY > 0) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };
        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    const hasLink = url !== null;
    const hasClients = clients !== null && clients.length > 0;
    const hasAgencies = agencies !== null && agencies.length > 0;
    const hasBroadcasters = broadcasters !== null && broadcasters.length > 0;
    const hasCredits = credits !== null && credits.length > 0;
    const hasAwards = awards !== null && awards.length > 0;
    const hasSidebar = hasClients || hasLink || hasBroadcasters;

    const onImageLoad = useCallback(() => {
        setImageLoaded(true);
    }, [setImageLoaded]);

    const textHeight = useMemo(() => {
        if (imageRef.current) {
            const { top = 0, height = 0 } = imageRef.current.getBoundingClientRect() || {};
            return top + height;
        }
        return null;
    }, [imageRef.current, scrolled, imageLoaded, width]);

    const tags = (sections || [])
        .map(({ title: sectionTitle = null }) => ({ label: sectionTitle }))
        .concat(categories || [])
        .concat(markets || [])
        .concat(platforms || []);

    const link = hasLink ? (
        <RoundedButton
            className={classNames([
                styles.link,
                { [styles.withTopBorder]: !hasClients && !hasBroadcasters },
            ])}
            href={url}
            external
        >
            {label !== null ? (
                label
            ) : (
                <FormattedMessage
                    defaultMessage="Go to website"
                    description="External link label"
                />
            )}
        </RoundedButton>
    ) : null;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            {backgroundText !== null ? (
                <div className={styles.backgroundContainer}>
                    <BackgroundText
                        className={styles.background}
                        text={backgroundText}
                        scrolled={scrolled}
                        maxHeight={textHeight}
                        colors={colors}
                    />
                </div>
            ) : null}
            <div ref={imageRef} className={styles.imageContainer}>
                <div
                    className={classNames([
                        styles.imageFrame,
                        {
                            [styles[logoAlignment]]: typeof styles[logoAlignment] !== 'undefined',
                        },
                    ])}
                >
                    {headerImage !== null || image !== null ? (
                        <Image
                            className={styles.image}
                            media={image || headerImage}
                            onLoad={onImageLoad}
                            loading="eager"
                        />
                    ) : null}
                </div>
            </div>
            <div className={styles.titleContainer}>
                <h1 className={styles.title}>{title}</h1>
                {subtitle !== null ? <h4 className={styles.subtitle}>{subtitle}</h4> : null}
                {tags !== null && tags.length > 0 ? (
                    <div
                        className={styles.categories}
                        style={{ color: elementsColor === 'black' ? 'white' : 'black' }}
                    >
                        {tags.map(({ label: categoryLabel = null }, i) => (
                            <RoundedButton
                                key={`button-${categoryLabel}-${i + 1}`}
                                className={styles.category}
                                active
                                disabled
                                invert={elementsColor === 'black'}
                            >
                                {categoryLabel}
                            </RoundedButton>
                        ))}
                    </div>
                ) : null}
                <div className={styles.summaryContainer}>
                    {summary !== null && (
                        <Body
                            className={classNames([
                                styles.summary,
                                { [styles.withMargin]: hasSidebar },
                            ])}
                            body={summary}
                        />
                    )}
                    {hasSidebar ? (
                        <div className={styles.sidebar}>
                            <div className={styles.lists}>
                                {!hasClients && !hasBroadcasters ? link : null}
                                {hasBroadcasters ? (
                                    <LinedList
                                        className={styles.list}
                                        title={
                                            <FormattedMessage
                                                defaultMessage="{count, plural, one {Broadcaster} other {Broadcasters}}"
                                                description="Section label"
                                                values={{
                                                    count: broadcasters.length,
                                                }}
                                            />
                                        }
                                        items={broadcasters}
                                        singleLine
                                        link={!hasClients ? link : null}
                                    />
                                ) : null}
                                {hasClients ? (
                                    <LinedList
                                        className={styles.list}
                                        // component={Client}
                                        title={
                                            <FormattedMessage
                                                defaultMessage="{count, plural, one {Client} other {Clients}}"
                                                description="Section label"
                                                values={{
                                                    count: clients.length,
                                                }}
                                            />
                                        }
                                        items={clients}
                                        singleLine
                                        link={!hasBroadcasters ? link : null}
                                    />
                                ) : null}
                                {hasAgencies ? (
                                    <LinedList
                                        className={styles.list}
                                        title={
                                            <FormattedMessage
                                                defaultMessage="{count, plural, one {Agency} other {Agencies}}"
                                                description="Section label"
                                                values={{
                                                    count: agencies.length,
                                                }}
                                            />
                                        }
                                        items={agencies}
                                        singleLine
                                    />
                                ) : null}
                                {subtitle !== null && renderedFormats !== null ? (
                                    <LinedList
                                        className={styles.list}
                                        title={
                                            <FormattedMessage
                                                defaultMessage="{count, plural, one {Format} other {Formats}}"
                                                description="Section label"
                                                values={{
                                                    count: 1,
                                                }}
                                            />
                                        }
                                        items={[{ name: renderedFormats }]}
                                    />
                                ) : null}
                                {hasCredits ? (
                                    <LinedList
                                        className={styles.list}
                                        component={ProjectCredit}
                                        items={credits}
                                    />
                                ) : null}
                                {hasAwards ? (
                                    <LinedList
                                        className={styles.list}
                                        component={ProjectAward}
                                        title={
                                            <FormattedMessage
                                                defaultMessage="{count, plural, one {Award} other {Awards}}"
                                                description="Section label"
                                                values={{
                                                    count: awards.length,
                                                }}
                                            />
                                        }
                                        items={awards}
                                    />
                                ) : null}
                            </div>
                            {}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

ProjectHeader.propTypes = propTypes;
ProjectHeader.defaultProps = defaultProps;

export default ProjectHeader;
